import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import auth from "../auth/authService";
import {
  Box,
  Grid,
  Typography,
  Stack,
  Card,
  CardContent,
  Paper,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
  Button,
} from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import TuneIcon from "@mui/icons-material/Tune";
import HistoryIcon from "@mui/icons-material/History";
import PeopleAltIcon from "@mui/icons-material/PeopleAlt";
import AddIcon from "@mui/icons-material/Add";

import {
  Area,
  AreaChart,
  XAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

import axios from "../api/axiosConfig";
import DH from "../utils/DataHelper";

export default function Dashboard() {
  const profile = auth.getProfile();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const [isloading, setIsLoading] = useState(false)
  const [rows, setRows] = useState(null)
  const [range, setRange] = useState("monthly");


  // ---------------------
  // 🔥 API CALL HERE
  // ------------------------
  const fetchData = async (query = {}) => {
    try {
      setIsLoading(true);

      const url = `/dashboard.php?reporttype=dashboard&range=${range}`;

      const resp = await axios.get(url);
      setRows(resp.data.data); // API must return array of rows
    } catch (err) {
      console.error("Error fetching data:", err);
    } finally {
      setIsLoading(false);
    }
  };


  // Load initial data
  useEffect(() => {
    fetchData();
  }, [range]);

  const handleLogout = () => {
    auth.logout();
    navigate("/login");
  };


  const headerGradient = isDark
    ? "linear-gradient(120deg, rgba(15,23,42,0.9), rgba(30,41,59,0.7))"
    : "linear-gradient(120deg, rgba(248,250,252,0.95), rgba(226,232,240,0.7))";

  const cardSurface = isDark ? "#0f172a" : "#ffffff";
  const cardBorder = isDark ? alpha("#ffffff", 0.08) : "#e2e8f0";

  return (
    <>
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Header */}
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 0,
            // border: "1px solid",
            // borderColor: cardBorder,
            background: headerGradient,
            color: isDark ? "#f8fafc" : "#0f172a",
          }}
        >
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "flex-start", md: "center" }}
            >
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 1, opacity: 0.7 }}>
                  Dashboard
                </Typography>
                <Typography variant="h4" fontWeight={700} sx={{ mt: 0.5 }}>
                  Welcome, {profile?.data.displayname || "User"}
                </Typography>
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                  Ringkasan cepat performa pembayaran dan aktivitas jemaat.
                </Typography>
              </Box>
              <Box flex={1} />
              <ToggleButtonGroup
                color="primary"
                value={range}
                exclusive
                size="small"
                onChange={(e, value) => value && setRange(value)}
                sx={{
                  backgroundColor: isDark ? alpha("#ffffff", 0.04) : "#ffffff",
                  border: "1px solid",
                  borderColor: cardBorder,
                  borderRadius: 2,
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    fontWeight: 600,
                    px: 2,
                  },
                }}
              >
                <ToggleButton value="weekly">Weekly</ToggleButton>
                <ToggleButton value="monthly">Monthly</ToggleButton>
                <ToggleButton value="yearly">Yearly</ToggleButton>
              </ToggleButtonGroup>
            </Stack>
          </CardContent>
        </Card>


        {/* <Typography>{JSON.stringify(rows)}</Typography> */}
        {/* Statistics Section */}
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 6, md: 3 }}>
            <KotakStatistik
              label="Total Member"
              value={DH.formatValue("N", rows?.cards.jumlah_jemaat)}
              loading={isloading}
              surface={cardSurface}
              border={cardBorder}
              gradient="linear-gradient(135deg,#2563eb,#0ea5e9)"
              trend={rows?.cards?.trend_jumlah_jemaat}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <KotakStatistik
              label="Current Year"
              value={DH.formatValue("N", rows?.cards.total_iuran)}
              loading={isloading}
              surface={cardSurface}
              border={cardBorder}
              gradient="linear-gradient(135deg,#22c55e,#16a34a)"
              trend={rows?.cards?.trend_total_iuran}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <KotakStatistik
              label="Bulan Ini"
              value={DH.formatValue("N", rows?.cards.iuran_bulan_ini)}
              loading={isloading}
              surface={cardSurface}
              border={cardBorder}
              gradient="linear-gradient(135deg,#f59e0b,#f97316)"
              trend={rows?.cards?.trend_iuran_bulan_ini}
            />
          </Grid>
          <Grid size={{ xs: 6, md: 3 }}>
            <KotakStatistik
              label="Hari Ini"
              value={DH.formatValue("N", rows?.cards.iuran_hari_ini)}
              loading={isloading}
              surface={cardSurface}
              border={cardBorder}
              gradient="linear-gradient(135deg,#8b5cf6,#6366f1)"
              trend={rows?.cards?.trend_iuran_hari_ini}
            />
          </Grid>
        </Grid>
        <Grid container spacing={1.5} sx={{ mt: 2 }}>
          <Grid size={{ xs: 12 }}>
            <Card
              elevation={0}
              sx={{
                borderRadius: 0,
                border: "1px solid",
                borderColor: cardBorder,
                background: cardSurface,
              }}
            >
              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1.5,
                }}
              >
                <Stack spacing={0.2}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Quick Actions
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Akses cepat ke fitur utama
                  </Typography>
                </Stack>
                <Box flex={1} />
                <Button
                  variant="contained"
                  startIcon={<TuneIcon />}
                  onClick={() => navigate("/paymenttransact")}
                >
                  Transaksi Pembayaran
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<HistoryIcon />}
                  onClick={() => navigate("/paymenthistory")}
                >
                  Riwayat Pembayaran
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PeopleAltIcon />}
                  onClick={() => navigate("/jemaat")}
                >
                  Data Jemaat
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={() => navigate("/newmember")}
                >
                  Tambah Member
                </Button>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {/* 


        

        {/* Chart */}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid size={{ xs: 12, lg: 6 }}>
            <ChartCard
              title="Payments Bulanan"
              subtitle="Total completed payments per month"
              loading={isloading}
              surface={cardSurface}
              border={cardBorder}
              gradientId="areaPaidMonthly"
              areaColor="#16a34a"
              areaTop="#22c55e"
              areaBottom="#16a34a"
              data={rows?.charts.monthly_payment}
              xKey="month"
            />

          </Grid>
          {/* CHART WEEKLY */}
          <Grid size={{ xs: 12, lg: 6 }}>

            <ChartCard
              title="Payment Mingguan"
              subtitle="Total completed payments per pekan"
              loading={isloading}
              surface={cardSurface}
              border={cardBorder}
              gradientId="areaPaidWeekly"
              areaColor="#84cc16"
              areaTop="#c4e35c"
              areaBottom="#65a30d"
              data={rows?.charts.weekly_payment}
              xKey="weeknum"
            />
          </Grid>
        </Grid>
      </Box >
    </ >
  );
}


function KotakStatistik({
  label,
  value,
  loading,
  surface = "background.paper",
  border = "divider",
  gradient = "linear-gradient(135deg,#4f46e5,#9333ea)",
  trend,
}) {
  const trendValue =
    typeof trend === "number"
      ? `${trend >= 0 ? "+" : ""}${trend.toFixed(1)}%`
      : null;
  const trendColor = trend >= 0 ? "success.main" : "error.main";
  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: border,
        backgroundColor: surface,
        position: "relative",
        overflow: "hidden",
        transition: "all .2s ease",

        "&:hover": {
          backgroundColor: "action.hover",
          transform: 'translateY(-1px)',
          boxShadow: "0 6px 18px rgba(0,0,0,0.08)",

        },


      }}
    >

      {/* Gradient accent */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          height: 4,
          width: "100%",
          background: gradient,
        }}
      />

      <Stack spacing={0.5}>
        <Typography
          variant="caption"
          color="text.secondary"
          fontWeight={600}
          sx={{ textTransform: "uppercase", letterSpacing: 0.5 }}
        >
          {label}
        </Typography>

        {loading ? (
          <Skeleton variant="text" height={34} width="60%" />
        ) : (
          <Typography variant="h5" fontWeight={700}>
            {value}
          </Typography>
        )}
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              backgroundColor: trendValue ? trendColor : "text.disabled",
            }}
          />
          <Typography variant="caption" color={trendValue ? trendColor : "text.disabled"}>
            {trendValue || "No trend data"}
          </Typography>
        </Stack>
      </Stack>
    </Paper>
  );
}

function ChartCard({
  title,
  subtitle,
  loading,
  surface,
  border,
  gradientId,
  areaColor,
  areaTop,
  areaBottom,
  data,
  xKey,
}) {
  return (
    <Card
      sx={{
        p: 3,
        my: 2,
        border: "1px solid",
        borderColor: border,
        borderRadius: 3,
        background: surface,
        "&:hover": {
          backgroundColor: "action.hover",
          transform: "translateY(-1px)",
          boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
        },
      }}
    >
      <Typography variant="h6" fontWeight={600} mb={0.5}>
        {title}
      </Typography>

      <Typography variant="body2" color="text.secondary" mb={3}>
        {subtitle}
      </Typography>

      <Box sx={{ width: "100%", height: 220 }}>
        {loading ? (
          <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
        ) : !data || data.length === 0 ? (
          <ChartEmptyState />
        ) : (
          <ResponsiveContainer>
            <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={areaTop} stopOpacity={0.35} />
                  <stop offset="100%" stopColor={areaBottom} stopOpacity={0.04} />
                </linearGradient>
              </defs>

              <XAxis
                dataKey={xKey}
                tick={{ fill: "#94a3b8", fontSize: 12 }}
                axisLine={false}
                tickLine={false}
              />

              <Tooltip
                formatter={(value) => [`Rp ${DH.formatValue("N", value)}`, "Pembayaran"]}
                contentStyle={{
                  background: "#ffffff",
                  borderRadius: 10,
                  border: "1px solid #e5e7eb",
                  boxShadow: "0 6px 16px rgba(0,0,0,0.12)",
                  fontSize: 13,
                }}
                labelStyle={{ fontWeight: 600 }}
              />

              <Area
                type="monotone"
                dataKey="paid"
                stroke={areaColor}
                strokeWidth={3}
                fill={`url(#${gradientId})`}
                dot={false}
                activeDot={{ r: 6 }}
                animationDuration={800}
              />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </Box>
    </Card>
  );
}

function ChartEmptyState() {
  return (
    <Box
      sx={{
        height: "100%",
        borderRadius: 2,
        border: "1px dashed",
        borderColor: "divider",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexDirection: "column",
        gap: 0.5,
        color: "text.secondary",
        background: "rgba(148,163,184,0.08)",
      }}
    >
      <Typography variant="subtitle2" fontWeight={600}>
        No data available
      </Typography>
      <Typography variant="caption">
        Data belum tersedia untuk periode ini
      </Typography>
    </Box>
  );
}
